import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
	ForecastSnapshotDataPointResponseApi,
	ForecastSnapshotListResponseApi,
} from "@/api/models/forecastSnapshot";
import type { VarianceAnalysisResponseApi } from "@/api/models/varianceAnalysis";
import {
	createForecastSnapshot,
	deleteForecastSnapshot,
	getForecastSnapshotDataPoints,
	getForecastSnapshots,
} from "@/api/services/forecastSnapshotService";
import { getVarianceAnalysis } from "@/api/services/varianceAnalysisService";

export const varianceAnalysisKeys = {
	all: ["varianceAnalysis"] as const,
	detail: (period: number, snapshotId?: number) =>
		["varianceAnalysis", period, snapshotId] as const,
};

export const snapshotKeys = {
	all: ["forecastSnapshots"] as const,
	dataPoints: (id: number) => ["forecastSnapshots", id, "dataPoints"] as const,
};

export function useVarianceAnalysis(period: number, snapshotId?: number) {
	return useQuery<VarianceAnalysisResponseApi>({
		queryKey: varianceAnalysisKeys.detail(period, snapshotId),
		queryFn: () => getVarianceAnalysis(period, snapshotId),
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
}

export function useForecastSnapshots(enabled = true) {
	return useQuery<ForecastSnapshotListResponseApi>({
		queryKey: snapshotKeys.all,
		queryFn: getForecastSnapshots,
		staleTime: 5 * 60 * 1000,
		enabled,
	});
}

export function useCreateForecastSnapshot() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createForecastSnapshot,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: snapshotKeys.all });
			queryClient.invalidateQueries({ queryKey: varianceAnalysisKeys.all });
		},
	});
}

export function useDeleteForecastSnapshot() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteForecastSnapshot,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: snapshotKeys.all });
			queryClient.invalidateQueries({ queryKey: varianceAnalysisKeys.all });
		},
	});
}

export function useForecastSnapshotDataPoints(snapshotId: number | undefined) {
	return useQuery<ForecastSnapshotDataPointResponseApi[]>({
		queryKey: snapshotKeys.dataPoints(snapshotId!),
		queryFn: () => getForecastSnapshotDataPoints(snapshotId!),
		enabled: snapshotId !== undefined,
		staleTime: 5 * 60 * 1000,
	});
}

export function useFilteredSnapshotTotals(
	dataPoints: ForecastSnapshotDataPointResponseApi[] | undefined,
	periodDays: number,
) {
	return useMemo(() => {
		if (!dataPoints || dataPoints.length === 0) {
			return null;
		}

		const now = new Date();
		const cutoff = new Date(now);
		cutoff.setDate(cutoff.getDate() - periodDays);

		const filtered = dataPoints.filter((dp) => {
			const d = new Date(dp.date);
			return d >= cutoff && d <= now;
		});

		const totalInflow = filtered.reduce((sum, dp) => sum + dp.incoming, 0);
		const totalOutflow = filtered.reduce((sum, dp) => sum + dp.outgoing, 0);

		return {
			totalInflow,
			totalOutflow,
			netCashFlow: totalInflow - totalOutflow,
			count: filtered.length,
		};
	}, [dataPoints, periodDays]);
}
